import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy,
  Plus,
  Search,
  Filter,
  Image,
  FileText,
  Calendar,
  MapPin,
  Edit2,
  Trash2,
  Eye,
  Star,
  Users
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import CustomSelect from '../components/common/CustomSelect';
import { sanitizeInput } from '../utils/inputSanitization';

const AdminResults = () => {
  const { isDarkMode } = useTheme();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Categories - Updated to match actual categories
  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: "Men's Open", label: "Men's Open" },
    { value: "Men's Mid-Am", label: "Men's Mid-Am" },
    { value: "Men's Senior", label: "Men's Senior" },
    { value: 'Junior Boys', label: 'Junior Boys' },
    { value: "Ladies Open", label: "Ladies Open" },
    { value: "Ladies Mid-Am", label: "Ladies Mid-Am" },
    { value: "Ladies Senior", label: "Ladies Senior" },
    { value: 'Junior Girls', label: 'Junior Girls' },
    { value: 'Mixed', label: 'Mixed' }
  ];

  // Status options
  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'published', label: 'Published' },
    { value: 'draft', label: 'Draft' },
    { value: 'archived', label: 'Archived' }
  ];

  // Fetch results from API
  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api.fsgu.co.za/get-results.php?status=all');
      const data = await response.json();
      
      if (data.success && data.data) {
        setResults(data.data);
      } else {
        console.error('Failed to fetch results:', data.error);
        setResults([]);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter results
  const filteredResults = results.filter(result => {
    const matchesSearch = 
      result.tournament_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.winner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.venue?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || result.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || result.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this result?')) return;
    
    try {
      const response = await fetch('https://api.fsgu.co.za/delete-result.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Result deleted successfully');
        fetchResults();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting result:', error);
      alert('Failed to delete result');
    }
  };

  // Handle toggle featured
  const handleToggleFeatured = async (id, currentStatus) => {
    try {
      const response = await fetch('https://api.fsgu.co.za/update-result.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          id,
          is_featured: currentStatus ? 0 : 1
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        fetchResults();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('Failed to toggle featured status');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-fsgu-green-100 text-fsgu-green-700 dark:bg-fsgu-green-900/30 dark:text-fsgu-green-300';
      case 'draft':
        return 'bg-fsgu-gold-100 text-fsgu-gold-700 dark:bg-fsgu-gold-900/30 dark:text-fsgu-gold-300';
      case 'archived':
        return 'bg-fsgu-silver-200 text-fsgu-silver-700 dark:bg-fsgu-gray-700 dark:text-fsgu-silver-400';
      default:
        return 'bg-fsgu-blue-100 text-fsgu-blue-700 dark:bg-fsgu-blue-900/30 dark:text-fsgu-blue-300';
    }
  };

  // Get category badge color
  const getCategoryColor = (category) => {
    if (category?.includes("Men's")) {
      return 'bg-fsgu-blue-100 text-fsgu-blue-700 dark:bg-fsgu-blue-900/30 dark:text-fsgu-blue-300';
    } else if (category?.includes("Ladies")) {
      return 'bg-fsgu-pink-100 text-fsgu-pink-700 dark:bg-fsgu-pink-900/30 dark:text-fsgu-pink-300';
    } else if (category?.includes("Junior")) {
      return 'bg-fsgu-green-100 text-fsgu-green-700 dark:bg-fsgu-green-900/30 dark:text-fsgu-green-300';
    } else if (category === 'Mixed') {
      return 'bg-fsgu-gold-100 text-fsgu-gold-700 dark:bg-fsgu-gold-900/30 dark:text-fsgu-gold-300';
    }
    return 'bg-fsgu-silver-200 text-fsgu-silver-700 dark:bg-fsgu-gray-700 dark:text-fsgu-silver-300';
  };

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      <div className="container-custom py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Tournament Results
            </h1>
            <p className={`text-sm mt-1 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Manage tournament results, winners, and reports
            </p>
          </div>
          
          <Link
            to="/admin/results/add"
            className="btn btn-primary inline-flex items-center justify-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Result
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`p-4 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Total Results
              </span>
              <Trophy className={`w-5 h-5 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`} />
            </div>
            <div className={`text-2xl font-bold ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              {results.length}
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Published
              </span>
              <Eye className={`w-5 h-5 ${
                isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-500'
              }`} />
            </div>
            <div className={`text-2xl font-bold ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              {results.filter(r => r.status === 'published').length}
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Drafts
              </span>
              <FileText className={`w-5 h-5 ${
                isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
              }`} />
            </div>
            <div className={`text-2xl font-bold ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              {results.filter(r => r.status === 'draft').length}
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Featured
              </span>
              <Star className={`w-5 h-5 ${
                isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
              }`} />
            </div>
            <div className={`text-2xl font-bold ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              {results.filter(r => r.is_featured).length}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`p-6 rounded-xl border ${
          isDarkMode
            ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
            : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
        }`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className={`w-5 h-5 ${
                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'
                }`} />
              </div>
              <input
                type="text"
                placeholder="Search tournaments, winners, venues..."
                value={searchTerm}
                onChange={(e) => {
                  const sanitized = sanitizeInput(e.target.value, 'text');
                  setSearchTerm(sanitized);
                }}
                className="input pl-10"
              />
            </div>

            {/* Category Filter */}
            <CustomSelect
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              options={categories}
              placeholder="All Categories"
              icon={<Filter className="w-5 h-5" />}
            />

            {/* Status Filter */}
            <CustomSelect
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={statusOptions}
              placeholder="All Status"
            />
          </div>
        </div>

        {/* Results Table */}
        <div className={`rounded-xl border overflow-hidden ${
          isDarkMode
            ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
            : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
        }`}>
          {loading ? (
            <div className="p-8 text-center">
              <div className={`inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-current border-r-transparent ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`}></div>
              <p className={`mt-2 ${isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}`}>
                Loading results...
              </p>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="p-8 text-center">
              <Trophy className={`w-12 h-12 mx-auto mb-3 ${
                isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
              }`} />
              <p className={`${isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}`}>
                No results found
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`border-b ${
                  isDarkMode ? 'border-fsgu-gray-700 bg-fsgu-gray-900/50' : 'border-fsgu-silver-200 bg-fsgu-silver-50'
                }`}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Tournament
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Winner
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Category
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Date
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Status
                    </th>
                    <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${
                  isDarkMode ? 'divide-fsgu-gray-700' : 'divide-fsgu-silver-200'
                }`}>
                  {filteredResults.map((result) => (
                    <tr
                      key={result.id}
                      className={`transition-colors duration-200 ${
                        isDarkMode
                          ? 'hover:bg-fsgu-gray-700/50'
                          : 'hover:bg-fsgu-silver-50'
                      }`}
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-start space-x-3">
                          {result.is_featured && (
                            <Star className="w-4 h-4 text-fsgu-gold-500 flex-shrink-0 mt-1" fill="currentColor" />
                          )}
                          <div>
                            <div className={`font-medium ${
                              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                            }`}>
                              {result.tournament_name}
                            </div>
                            <div className={`text-sm flex items-center mt-1 ${
                              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                            }`}>
                              <MapPin className="w-3 h-3 mr-1" />
                              {result.venue}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className={`font-medium ${
                          isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-800'
                        }`}>
                          {result.winner_name}
                        </div>
                        <div className={`text-sm ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          {result.winner_score}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getCategoryColor(result.category)
                        }`}>
                          {result.category}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className={`flex items-center text-sm ${
                          isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                        }`}>
                          <Calendar className="w-4 h-4 mr-1.5" />
                          {formatDate(result.tournament_date)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getStatusColor(result.status)
                        }`}>
                          {result.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleToggleFeatured(result.id, result.is_featured)}
                            className={`p-2 rounded-lg transition-colors ${
                              result.is_featured
                                ? isDarkMode
                                  ? 'bg-fsgu-gold-500/20 text-fsgu-gold-400 hover:bg-fsgu-gold-500/30'
                                  : 'bg-fsgu-gold-500/10 text-fsgu-gold-600 hover:bg-fsgu-gold-500/20'
                                : isDarkMode
                                ? 'bg-fsgu-gray-700 text-fsgu-silver-400 hover:bg-fsgu-gray-600'
                                : 'bg-fsgu-silver-100 text-fsgu-silver-600 hover:bg-fsgu-silver-200'
                            }`}
                            title={result.is_featured ? 'Remove from featured' : 'Add to featured'}
                          >
                            <Star className="w-4 h-4" fill={result.is_featured ? 'currentColor' : 'none'} />
                          </button>
                          
                          <Link
                            to={`/admin/results/edit/${result.id}`}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode
                                ? 'bg-fsgu-gray-700 text-fsgu-silver-300 hover:bg-fsgu-gray-600'
                                : 'bg-fsgu-silver-100 text-fsgu-silver-600 hover:bg-fsgu-silver-200'
                            }`}
                            title="Edit result"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Link>
                          
                          <button
                            onClick={() => handleDelete(result.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode
                                ? 'bg-fsgu-red-500/20 text-fsgu-red-400 hover:bg-fsgu-red-500/30'
                                : 'bg-fsgu-red-500/10 text-fsgu-red-600 hover:bg-fsgu-red-500/20'
                            }`}
                            title="Delete result"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResults;